import { Injectable, NgZone } from '@angular/core';
import {
  Auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  applyActionCode,
  signOut,
  sendPasswordResetEmail,
  signInWithPopup,
  authState,
  GoogleAuthProvider,
} from '@angular/fire/auth';
import { Firestore, doc, setDoc, getDoc } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { Timestamp } from 'firebase/firestore';

export interface Profile {
  firstname: string,
  lastname: string,
  company: string,
  emailMarketingConsent: boolean
  createdAt: Timestamp;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  constructor(
    public afs: Firestore, // Inject Firestore service
    public afAuth: Auth, // Inject Firebase auth service
    public router: Router,
    public ngZone: NgZone // NgZone service to remove outside scope warning
  ) {}

  // Sign in with email/password
  async signIn(email: string, password: string) {
    var result = await signInWithEmailAndPassword(this.afAuth, email, password);
    if (!result.user?.emailVerified) {
      throw new Error("auth/email-not-verified");
    }
    const token = await this.getUserToken(result.user?.email!);
    localStorage.setItem('user', JSON.stringify(result.user));
    localStorage.setItem('token', token);

    authState(this.afAuth).subscribe((user) => {
      console.log(`authState updated with: ${JSON.stringify(user)}`);
      if (!user) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    });

    await this.ngZone.run(async () => {
      await this.router.navigate(['admin']);
    });
  }

  // Sign up with email/password
  async signUp(email: string, password: string, profile: Profile) {
    // we wan't to preserve form information first
    // before the create user flow kicks off as it expects this info
    await this.setUserProfile(email, profile);
    return createUserWithEmailAndPassword(this.afAuth, email, password)
      .then(async (credential) => {
        await this.sendVerificationMail(credential.user);
      });
  }

  // Send email verification when new user sign up
  sendVerificationMail(user = this.afAuth.currentUser!) {
    return sendEmailVerification(user)
      .then(async () => {
        localStorage.setItem('user', JSON.stringify({
          email: user.email
        }));
      })
      .then(() => {
        this.router.navigate(['verify-email-address']);
      });
  }

  // Send password reset email
  forgotPassword(passwordResetEmail: string) {
    return sendPasswordResetEmail(this.afAuth, passwordResetEmail);
  }

  // Returns true when user is logged in and email is verified
  get isLoggedIn(): boolean {
    const user = JSON.parse(localStorage.getItem('user')!);
    return user !== null && user.emailVerified !== false ? true : false;
  }

  get userData(): any {
    return JSON.parse(localStorage.getItem('user')!);
  }

  // Sign in with Google
  googleAuth() {
    return this.authLogin(new GoogleAuthProvider()).then((res: any) => {
      if (res) {
        this.router.navigate(['admin']);
      }
    });
  }

  // Auth logic to run auth providers
  authLogin(provider: any) {
    return signInWithPopup(this.afAuth, provider)
      .then((result) => {
        this.ngZone.run(() => {
          this.router.navigate(['admin']);
        });
      })
      .catch((error) => {
        window.alert(error);
      });
  }

  /* Setting up user data when sign in with username/password,
  sign up with username/password and sign in with social auth
  provider in Firestore database using modular Firestore doc/setDoc */
  setUserProfile(email: string, profile: Profile) {
    const userRef = doc(this.afs, `signups/${email.toLowerCase()}`);
    return setDoc(userRef, profile, {
      merge: true,
    });
  }

  async getUserToken(email: string) {
    const userRef = doc(this.afs, `users/${email}`);
    var user: any;
    // if this is a new registration, it may take a while before a new user profile gets
    // created. We need to loop till the REST API token is available

    for (var i=1; i<100; i++) {
      console.log(`Attempt ${i}: reading token.`);
      try {
        user = await getDoc(userRef);
        if(user.data() && user.data().token) {
          console.log(`Token retrieved.`);
          break;
        }
      } catch (e: any) {
        console.log(e);
      }
    }
    if (!user || !user.data()) {
      throw new Error('Unable to connect to ClearScape Analytics Experience backend. This usually means that your network configuration is not allowing connections to https://firestore.googleapis.com/. Please contact your network administrator to allow access to this url.');
    }
    return user!.data().token;
  }

  // Sign out
  signOut() {
    return signOut(this.afAuth).then(() => {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      this.router.navigate(['sign-in']);
    });
  }

  async handleVerifyEmail(actionCode: string)  {
    console.log(`actionCode: ${actionCode}`);
    await applyActionCode(this.afAuth, actionCode);
  }
}
